import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { RouterLink } from "@angular/router";
import { Subscription } from "rxjs";

import {
  CalidadCodigoRes,
  EstadoConexion,
  EstadoSincronizacionCc,
  ProyectoConfiguracion,
  Sincronizacion,
} from "src/app/shared/model/calidad-codigo/calidad-codigo.model";
import { CalidadCodigoService } from "../calidad-codigo.service";
import { ESTADO_SINCRONIZACION, EstadoCc, ORIGEN_SINCRONIZACION, estadoDe, textoDeError } from "../cc-estados";
import { aFecha, entero, fechaCorta, fechaHora, haceCuanto, hora, plural } from "../cc-formato";
import { FlotantesCcService } from "../flotantes/flotantes-cc.service";

/** Un renglón de la bitácora, armado UNA vez por respuesta. */
interface FilaBitacora {
  id: number;
  inicio: string;
  origen: string;
  duracion: string;
  proyectos: string;
  nuevos: string;
  cambios: string;
  llamadas: string;
  estado: EstadoCc<EstadoSincronizacionCc>;
  error: string;
}

/** Un renglón del estado de la conexión. */
interface DatoConexion {
  rotulo: string;
  valor: string;
  enlace?: string;
  tono: "ok" | "aviso" | "neutro";
}

const RUTA_TABLERO = "/admin/calidad-codigo/tablero";
/** Tope del campo «Etiqueta» (cos_sonar_proyecto.etiqueta VARCHAR(30)). */
const LARGO_ETIQUETA = 30;

/**
 * Calidad de código · Configuración (docs/CALIDAD_CODIGO.md §5), solo con
 * el perfil de Administración: el estado de la conexión con SonarQube (URL,
 * versión, si hay credencial y cuándo vence, nunca su valor; si la tarea
 * programada está encendida; la última actualización, sin contar las pasadas de un reporte), la bitácora de las últimas
 * 10 sincronizaciones y, por proyecto, lo que decide cómo se muestra en el
 * tablero: si se muestra, la etiqueta, los días para «desactualizado», la
 * liga con Azure DevOps (para el enlace al commit) y la rama real. Se guarda
 * proyecto por proyecto (configuracionGuardar.do).
 *
 * Base visual: admin/ajustes (.ajustes-page): hero con sus chips, tarjetas
 * con título e icono y los campos outline de table-page__field. La lista de
 * proyectos y el formulario del elegido van lado a lado para que la pantalla
 * quepa sin desplazarse.
 */
@Component({
  selector: "app-configuracion-cc",
  standalone: true,
  templateUrl: "./configuracion-cc.component.html",
  styleUrls: ["../calidad-codigo.shared.css", "./configuracion-cc.component.css"],
  imports: [CommonModule, FormsModule, RouterLink, MatFormFieldModule, MatInputModule, MatSlideToggleModule],
})
export class ConfiguracionCcComponent implements OnInit, OnDestroy {
  readonly rutaTablero = RUTA_TABLERO;
  readonly largoEtiqueta = LARGO_ETIQUETA;

  loading = false;
  error = "";
  sinPermiso = false;
  conexion: EstadoConexion | null = null;
  datosConexion: DatoConexion[] = [];
  bitacora: FilaBitacora[] = [];
  proyectos: ProyectoConfiguracion[] = [];
  /** La copia que se edita del proyecto elegido; se descarta al cambiar de proyecto sin guardar. */
  edicion: ProyectoConfiguracion | null = null;
  guardando = false;
  sincronizando = false;

  private consulta?: Subscription;
  private accion?: Subscription;
  private readonly ahora = new Date();

  constructor(
    private readonly servicio: CalidadCodigoService,
    private readonly cdr: ChangeDetectorRef,
    private readonly flotantes: FlotantesCcService,
  ) {}

  ngOnInit(): void {
    this.consultar();
  }

  ngOnDestroy(): void {
    this.consulta?.unsubscribe();
    this.accion?.unsubscribe();
  }

  /* ============================ Consulta ============================ */

  consultar(): void {
    this.consulta?.unsubscribe();
    this.loading = true;
    this.error = "";
    this.sinPermiso = false;
    this.cdr.detectChanges();
    this.consulta = this.servicio.configuracion().subscribe({
      next: (res) => {
        this.loading = false;
        if (res?.codigoError) {
          this.sinPermiso = res.codigoError === "COD_ERROR_00002";
          this.error = textoDeError(res, "consultar la configuración");
        } else {
          this.aplica(res);
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.loading = false;
        this.error = "No se pudo consultar la configuración: sin conexión con el servidor del COS.";
        this.cdr.detectChanges();
      },
    });
  }

  private aplica(res: CalidadCodigoRes): void {
    const c = res.configuracion;
    this.conexion = c?.conexion ?? res.conexion ?? null;
    this.datosConexion = this.armaConexion(this.conexion);
    this.bitacora = (c?.sincronizaciones ?? []).slice(0, 10).map((s) => this.filaBitacora(s));
    this.proyectos = c?.proyectos ?? [];
    const elegido = this.edicion ? this.proyectos.find((p) => p.id === this.edicion?.id) : undefined;
    this.elige(elegido ?? this.proyectos[0] ?? null);
  }

  /* ============================ Conexión ============================ */

  private armaConexion(c: EstadoConexion | null): DatoConexion[] {
    if (!c) {
      return [];
    }
    const datos: DatoConexion[] = [
      { rotulo: "Servidor", valor: c.urlWeb || "—", enlace: c.urlWeb || undefined, tono: "neutro" },
      { rotulo: "Versión de SonarQube", valor: c.version || "sin leer todavía", tono: c.version ? "neutro" : "aviso" },
      this.datoCredencial(c),
      {
        rotulo: "Sincronización programada",
        valor: c.syncActiva ? "Activa, con la frecuencia de cos.sonar.sync.cron (cada 15 minutos por omisión)" : "Apagada (cos.sonar.sync.activa=false): solo con «Actualizar»",
        tono: c.syncActiva ? "ok" : "aviso",
      },
      this.datoUltimaPasada(c),
    ];
    return datos;
  }

  private datoCredencial(c: EstadoConexion): DatoConexion {
    if (!c.configurada) {
      return { rotulo: "Credencial", valor: "Sin configurar (cos.sonar.token vacío)", tono: "aviso" };
    }
    if (!c.tokenVence) {
      return { rotulo: "Credencial", valor: "Configurada; sin fecha de vencimiento registrada (cos.sonar.token-vence)", tono: "ok" };
    }
    const vence = fechaCorta(`${c.tokenVence}T12:00:00Z`);
    return c.tokenPorVencer
      ? { rotulo: "Credencial", valor: `Configurada; vence el ${vence}: hay que renovarla`, tono: "aviso" }
      : { rotulo: "Credencial", valor: `Configurada; vence el ${vence}`, tono: "ok" };
  }

  private datoUltimaPasada(c: EstadoConexion): DatoConexion {
    if (!c.ultimaSync) {
      return { rotulo: "Última actualización", valor: "Ninguna todavía", tono: "aviso" };
    }
    const estado = estadoDe(ESTADO_SINCRONIZACION, c.ultimaSyncEstado);
    const cuando = `${fechaHora(c.ultimaSync)} (${haceCuanto(c.ultimaSync, this.ahora)})`;
    if (c.enCurso) {
      return { rotulo: "Última actualización", valor: `En curso desde las ${hora(c.ultimaSync)}`, tono: "neutro" };
    }
    const error = c.ultimaSyncError ? `: ${c.ultimaSyncError}` : "";
    return {
      rotulo: "Última actualización",
      valor: `${cuando} · ${estado.texto.toLowerCase()}${error}`,
      tono: c.ultimaSyncEstado === "OK" ? "ok" : "aviso",
    };
  }

  /** Los chips del hero: conexión, sincronización y última actualización. */
  get chips(): { texto: string; clase: string; icono: string }[] {
    const c = this.conexion;
    if (!c) {
      return [];
    }
    const lista = [
      c.configurada
        ? { texto: c.tokenPorVencer ? "Credencial por vencer" : "Credencial configurada", clase: c.tokenPorVencer ? "is-aviso" : "is-pasa", icono: "vpn_key" }
        : { texto: "Sin credencial", clase: "is-aviso", icono: "vpn_key_off" },
      c.syncActiva
        ? { texto: "Sincronización activa", clase: "is-pasa", icono: "sync" }
        : { texto: "Sincronización apagada", clase: "is-neutro", icono: "sync_disabled" },
    ];
    if (c.ultimaSync) {
      const estado = estadoDe(ESTADO_SINCRONIZACION, c.enCurso ? "EN_CURSO" : c.ultimaSyncEstado);
      lista.push({ texto: `Última actualización ${estado.texto.toLowerCase()} · ${haceCuanto(c.ultimaSync, this.ahora)}`, clase: estado.clase, icono: estado.icono });
    }
    return lista;
  }

  /** «Sincronizar ahora»: una pasada de los 12 proyectos; al terminar se vuelve a leer la bitácora. */
  sincronizar(): void {
    if (this.sincronizando) {
      return;
    }
    this.sincronizando = true;
    this.cdr.detectChanges();
    this.accion?.unsubscribe();
    this.accion = this.servicio.actualizar().subscribe({
      next: (res) => {
        this.sincronizando = false;
        const s = res?.sincronizacion;
        if (res?.codigoError) {
          this.flotantes.error(textoDeError(res, "sincronizar"));
        } else if (s?.estado === "EN_CURSO") {
          this.flotantes.info(`Ya hay una sincronización en curso desde las ${hora(s.inicio)}.`);
        } else if (s?.estado === "ERROR") {
          this.flotantes.error(`La sincronización no pudo con SonarQube: ${s.error || "sin respuesta"}.`);
        } else if (s) {
          this.flotantes.exito(`Sincronizado: ${entero(s.proyectos)} proyectos, ${entero(s.analisisNuevos)} análisis nuevos y ${entero(s.cambios)} cambios en ${entero(s.llamadas)} llamadas.`);
        }
        this.consultar();
      },
      error: () => {
        this.sincronizando = false;
        this.flotantes.error("No se pudo sincronizar: sin conexión con el servidor del COS.");
        this.cdr.detectChanges();
      },
    });
  }

  /* ============================ Bitácora ============================ */

  private filaBitacora(s: Sincronizacion): FilaBitacora {
    return {
      id: s.id,
      inicio: fechaHora(s.inicio),
      origen: [ORIGEN_SINCRONIZACION[s.origen] ?? s.origen, s.usuario].filter(Boolean).join(" · "),
      duracion: duracion(s.inicio, s.fin),
      proyectos: entero(s.proyectos),
      nuevos: entero(s.analisisNuevos),
      cambios: entero(s.cambios),
      llamadas: entero(s.llamadas),
      estado: estadoDe(ESTADO_SINCRONIZACION, s.estado),
      error: s.error ?? "",
    };
  }

  trackBitacora(_: number, f: FilaBitacora): number {
    return f.id;
  }

  /** "las últimas 10 pasadas" o, con menos, "3 pasadas". */
  get textoBitacora(): string {
    const n = this.bitacora.length;
    return n >= 10 ? "las últimas 10 pasadas" : plural(n, "pasada", "pasadas");
  }

  /* ============================ Proyectos ============================ */

  elige(p: ProyectoConfiguracion | null): void {
    this.edicion = p ? { ...p } : null;
  }

  get elegido(): ProyectoConfiguracion | null {
    return this.proyectos.find((p) => p.id === this.edicion?.id) ?? null;
  }

  /** Hay algo distinto de lo guardado. */
  get conCambios(): boolean {
    const e = this.edicion;
    const g = this.elegido;
    if (!e || !g) {
      return false;
    }
    return (["mostrar", "etiqueta", "diasDesactualizado", "azureUrl", "azureProyectoId", "azureRepoId", "azureDefSonar", "ramaReal"] as const)
      .some((k) => normaliza(e[k]) !== normaliza(g[k]));
  }

  guardar(): void {
    const e = this.edicion;
    if (!e || this.guardando) {
      return;
    }
    const dias = e.diasDesactualizado;
    if (dias != null && (!Number.isInteger(dias) || dias < 1 || dias > 3650)) {
      this.flotantes.error("Los días para «desactualizado» van de 1 a 3650; vacío usa el valor global del servidor.");
      return;
    }
    this.guardando = true;
    this.cdr.detectChanges();
    this.accion?.unsubscribe();
    this.accion = this.servicio.configuracionGuardar(limpia(e)).subscribe({
      next: (res) => {
        this.guardando = false;
        if (res?.codigoError) {
          this.flotantes.error(textoDeError(res, "guardar la configuración"));
          this.cdr.detectChanges();
          return;
        }
        if (res.configuracion) {
          this.aplica(res);
        }
        this.flotantes.exito(`Configuración de ${e.nombre} guardada.`);
        this.cdr.detectChanges();
      },
      error: () => {
        this.guardando = false;
        this.flotantes.error("No se pudo guardar: sin conexión con el servidor del COS.");
        this.cdr.detectChanges();
      },
    });
  }

  descartar(): void {
    this.elige(this.elegido);
  }

  trackProyecto(_: number, p: ProyectoConfiguracion): number {
    return p.id;
  }
}

/** "1.2 s", "3 min 20 s", "en curso". */
function duracion(inicio: string, fin: string | null | undefined): string {
  const a = aFecha(inicio);
  const b = aFecha(fin);
  if (!a || !b) {
    return "en curso";
  }
  const s = Math.max(0, (b.getTime() - a.getTime()) / 1000);
  if (s < 60) {
    return `${s < 10 ? s.toFixed(1) : Math.round(s)} s`;
  }
  return `${Math.floor(s / 60)} min ${Math.round(s % 60)} s`;
}

/** Vacío, nulo e indefinido cuentan igual al comparar. */
function normaliza(v: string | number | boolean | null | undefined): string {
  if (v == null || v === "") {
    return "";
  }
  return typeof v === "string" ? v.trim() : `${v}`;
}

/** Lo que viaja: textos sin espacios en los extremos y vacíos como nulos. */
function limpia(e: ProyectoConfiguracion): ProyectoConfiguracion {
  const texto = (v: string | null | undefined): string | null => (v == null || v.trim() === "" ? null : v.trim());
  return {
    id: e.id,
    clave: e.clave,
    nombre: e.nombre,
    mostrar: e.mostrar !== false,
    etiqueta: texto(e.etiqueta),
    diasDesactualizado: e.diasDesactualizado ?? null,
    azureUrl: texto(e.azureUrl),
    azureProyectoId: texto(e.azureProyectoId),
    azureRepoId: texto(e.azureRepoId),
    azureDefSonar: e.azureDefSonar ?? null,
    ramaReal: texto(e.ramaReal),
  };
}
